import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import LoadingSpinner from './components/LoadingSpinner';
import { PageErrorBoundary } from './components/common/ErrorBoundary';
import TokenSyncLayout from './components/TokenSyncLayout';

console.log('🟢 App.tsx - Module chargé');
import LoginPage from './pages/LoginPage';
import HomepageController from './components/HomepageController';
import QuizPage from './pages/QuizPage';
import ScorePage from './pages/ScorePage';
import ScorePage2 from './pages/ScorePage2';
import PlanPage from './pages/PlanPage';
import ThanksPage from './pages/ThanksPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import MonEspace from './pages/MonEspace';
import ProspectsList from './pages/admin/ProspectsList';
import ProspectDetail from './pages/admin/ProspectDetail';
import QuizManager from './pages/admin/QuizManager';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import SettingsPage from './pages/admin/SettingsPage';
import CampaignsPage from './pages/admin/CampaignsPage';
import MessagesPage from './pages/admin/MessagesPage';
import EmailCampaignsPage from './pages/admin/EmailCampaignsPage';
import AdminLayout from './components/admin/AdminLayoutOptimized';
import AIRecommendationsAdmin from './pages/admin/AIRecommendationsAdmin';
import AICitationsManager from './pages/admin/AICitationsManager';
import TestApiPage from './pages/admin/TestApi';
import TestEdgeFunctionPage from './pages/admin/TestEdgeFunction';
import TestKeywordsComponent from './components/admin/TestKeywordsComponent';
import PagePerformanceScore from './pages/PagePerformanceScore';
import ReferralPage from './pages/ReferralPage';
import ReferralsManagementPage from './pages/admin/ReferralsManagementPage';
import { ROUTES } from './config/urls';
import TestPageCopy from './pages/TestPage_copy';
import TestPage2 from './pages/TestPage2';
import HomePage from './pages/HomePage';
import LandingController from './components/LandingController';
import HeroDesignComparison from './pages/HeroDesignComparison';
import CtaSectionComparison from './pages/CtaSectionComparison';
import CtaSectionRedesigned from './pages/CtaSectionRedesigned';
import CmsInfoComparison from './pages/CmsInfoComparison';
import KeywordsPerformanceComparison from './pages/KeywordsPerformanceComparison';
import AiRecommendationsComparison from './features/testPage/AiRecommendationsComparison';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

function App() {
  const location = useLocation();
  
  useEffect(() => {
    console.log('🧭 Navigation vers:', location.pathname);
    console.log('🔍 Query params:', location.search);
    console.log('📍 Hash:', location.hash);
  }, [location]);

  console.log('🟢 App - Composant en cours de rendu');
  console.log('📍 Route actuelle:', location.pathname);

  return (
    <PageErrorBoundary>
      <TokenSyncLayout>
        <Routes>
        {/* Auth routes */}
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />

        {/* Public routes - Page d'accueil standard */}
        <Route path={ROUTES.HOME} element={<HomePage />} />
        {/* Route pour la page d'accueil standard */}
        <Route path={ROUTES.HOMEPAGE} element={<HomePage />} />
        {/* Route publique vers la page de test refactorisée */}
        <Route path={ROUTES.TEST_COPY()} element={<TestPageCopy />} />
        <Route path={ROUTES.TEST_COPY(':token')} element={<TestPageCopy />} />
        
        {/* Route pour TestPage2 - Version optimisée UI/UX */}
        <Route path="/testpage2" element={<TestPage2 />} />
        <Route path="/testpage2/:token" element={<TestPage2 />} />
        
        {/* Nouvelle route pour la landing page moderne */}
        <Route path="/landing/:token" element={<LandingController />} />
        
        {/* Route pour comparer les designs de hero section */}
        <Route path="/hero-comparison" element={<HeroDesignComparison />} />
        
        {/* Route pour comparer les sections CTA */}
        <Route path="/cta-comparison" element={<CtaSectionComparison />} />
        
        {/* Route pour la section CTA redesignée */}
        <Route path="/cta-redesigned" element={<CtaSectionRedesigned />} />
        
        {/* Route pour comparer les designs CMS Info */}
        <Route path="/cms-info-comparison" element={<CmsInfoComparison />} />
        
        {/* Route pour comparer les designs Keywords Performance */}
        <Route path="/keywords-performance-comparison" element={<KeywordsPerformanceComparison />} />
        
        {/* Route pour comparer les designs AI Recommendations */}
        <Route path="/ai-recommendations-comparison" element={<AiRecommendationsComparison />} />
        
        {/* Routes spécifiques AVANT la route catch-all */}
        <Route path={ROUTES.QUIZ()} element={<QuizPage />} />
        <Route path={ROUTES.QUIZ(':token')} element={<QuizPage />} />
        <Route path={ROUTES.SCORE()} element={<ScorePage />} />
        <Route path={ROUTES.SCORE(':token')} element={<ScorePage />} />
        <Route path="/monscore" element={<ScorePage2 />} />
        <Route path="/monscore/:token" element={<ScorePage2 />} />
        <Route path="/analyse" element={<TestPage2 />} />
        <Route path="/analyse/:token" element={<TestPage2 />} />
        <Route path={ROUTES.PLAN()} element={<PlanPage />} />
        <Route path={ROUTES.PLAN(':token')} element={<PlanPage />} />
        <Route path={ROUTES.THANKS()} element={<ThanksPage />} />
        <Route path={ROUTES.THANKS(':token')} element={<ThanksPage />} />

        {/* Route catch-all pour les tokens - DOIT être en dernier */}
        <Route path="/:token" element={<HomepageController />} />

        {/* Protected admin routes */}
        <Route path="/mon-espace" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/audit-requests" element={
          <ProtectedRoute>
            <AdminLayout>
              <MonEspace />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/dashboard" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/prospects" element={
          <ProtectedRoute>
            <ProspectsList />
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/prospect/:id" element={
          <ProtectedRoute>
            <ProspectDetail />
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/test-api" element={
          <ProtectedRoute>
            <TestApiPage />
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/test-edge" element={
          <ProtectedRoute>
            <TestEdgeFunctionPage />
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/quiz" element={
          <ProtectedRoute>
            <QuizManager />
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/analytics" element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/campaigns" element={
          <ProtectedRoute>
            <AdminLayout>
              <CampaignsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/messages" element={
          <ProtectedRoute>
            <AdminLayout>
              <MessagesPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/settings" element={
          <ProtectedRoute>
            <AdminLayout>
              <SettingsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/email-campaigns" element={
          <ProtectedRoute>
            <AdminLayout>
              <EmailCampaignsPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/ai-recommendations" element={
          <ProtectedRoute>
            <AIRecommendationsAdmin />
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/ai-citations" element={
          <ProtectedRoute>
            <AICitationsManager />
          </ProtectedRoute>
        } />
        <Route path="/mon-espace/referrals" element={
          <ProtectedRoute>
            <AdminLayout>
              <ReferralsManagementPage />
            </AdminLayout>
          </ProtectedRoute>
        } />
        
        {/* Route temporaire pour tester les mots-clés classés */}
        <Route path="/mon-espace/test-keywords" element={
          <ProtectedRoute>
            <AdminLayout>
              <TestKeywordsComponent />
            </AdminLayout>
          </ProtectedRoute>
        } />

        {/* Route pour l'analyse de performance de page */}
        <Route path="/prospects/:prospectId/page-performance/*" element={<PagePerformanceScore />} />

        {/* Route pour la redirection d'emails */}
        <Route path="/referral/:token" element={<ReferralPage />} />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
        </Routes>
      </TokenSyncLayout>
    </PageErrorBoundary>
  );
}

export default App
